import {useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject} from 'react'
import {SelectPanel, SkeletonBox} from '@primer/react'
import {CommentDiscussionIcon, PlusIcon} from '@primer/octicons-react'
import {ReposSelector} from '@github-ui/repos-selector'
import {scopedRepositoryQuery} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import {sendEvent} from '@github-ui/hydro-analytics'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {buildNewRepositoryUrl, queryForRepos} from '../task-chat-input-toolbar/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat-types'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'
import type {OnboardingAnchorId} from '@github-ui/copilot-shared-ui/components/onboarding/use-onboarding-anchor'

import {EnvironmentPicker} from './EnvironmentPicker'
import {CLOUD_ENVIRONMENT_ID, type EnvironmentOption} from './environment-option'
import {RepoBranchPill} from './RepoBranchPill'
import {ChatTargetPicker} from './ChatTargetPicker'
import {loadDestinationRepositories} from './load-destination-repositories'
import type {ChatOwnerControl} from './chat-owner-control'
import {isRepoInScope, repoPickerScopeSubtitle} from '../shared'
import styles from './EnvironmentStrip.module.css'
import {EnvironmentStripTour, isEnvironmentStripTourEnabled} from './EnvironmentStripTour'
import {failedRepositoryLoad} from './failed-repository-load'

const ignoreTourVisibility = () => {}

// Keep enabled stable during a mount; changing the wrapper remounts the picker.
function OnboardingAnchor({enabled, id, children}: {enabled: boolean; id: OnboardingAnchorId; children: ReactNode}) {
  return enabled ? (
    <div className={styles.anchor} data-copilot-onboarding-anchor={id}>
      {children}
    </div>
  ) : (
    children
  )
}

export interface EnvironmentStripProps {
  hideRepoSelector?: boolean
  initialRepoNWO?: string
  initialBranch?: string
  targetSelection: TaskComposerTargetSelection
  onTargetSelectionChange: (selection: TaskComposerTargetSelection) => void
  selectedBranch: string | undefined
  onBranchSelect: (branch: string | undefined) => void
  currentPrompt?: string
  /** Login of the current user, used to pre-fill the owner on the new repository URL. */
  currentUserLogin?: string
  selectedEnvironment: EnvironmentOption | null
  /** Effective environment when a launcher supplies a destination outside the stored selection. */
  selectedOption?: EnvironmentOption | null
  design?: 'default' | 'simplified'
  onSelectEnvironment: (env: EnvironmentOption) => void
  showEnvironmentPicker?: boolean
  /** Forwarded to the environment picker; adds the managed-sandbox option. */
  includeSandboxOption?: boolean
  /**
   * Replaces the picker's default option list. A host that can start a session
   * in only some environments passes just those, so the picker never offers a
   * destination the host has no submit for.
   */
  environmentOptions?: EnvironmentOption[]
  /** Enables the Sandbox-only repo-less Chat mode for callers that support it. */
  allowChat?: boolean
  disabled?: boolean
  scopePicker?: ReactNode
  /**
   * Filters the repo picker strictly by owner. Null blocks repository loading
   * until the scoped owner resolves; undefined preserves unscoped environments.
   */
  scopeOwner?: string | null
  preferredRepositoryOwner?: string
  sandboxDisabledOwners?: readonly string[]
  chatOwner?: ChatOwnerControl
  suppressAutoSelect?: boolean
  showTour?: boolean
  submitting?: boolean
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  onTourVisibilityChange?: (visible: boolean) => void
}

export function EnvironmentStrip({
  hideRepoSelector,
  initialRepoNWO,
  initialBranch,
  targetSelection,
  onTargetSelectionChange,
  selectedBranch,
  onBranchSelect,
  currentPrompt,
  currentUserLogin,
  selectedEnvironment,
  selectedOption,
  design = 'default',
  onSelectEnvironment,
  showEnvironmentPicker = false,
  includeSandboxOption = false,
  environmentOptions,
  allowChat = false,
  disabled = false,
  scopePicker,
  scopeOwner,
  preferredRepositoryOwner,
  sandboxDisabledOwners,
  chatOwner,
  suppressAutoSelect = false,
  showTour = false,
  submitting = false,
  textAreaRef,
  onTourVisibilityChange,
}: EnvironmentStripProps) {
  const tourEnabled = showTour && isEnvironmentStripTourEnabled()
  const [portalSlots, setPortalSlots] = useState<Partial<Record<OnboardingAnchorId, HTMLDivElement>>>({})
  const registerTourPortal = useCallback((node: HTMLDivElement | null, id: OnboardingAnchorId) => {
    setPortalSlots(previous => {
      if (previous[id] === (node ?? undefined)) return previous
      return {...previous, [id]: node ?? undefined}
    })
  }, [])
  const setChatPortalSlot = useCallback(
    (node: HTMLDivElement | null) => registerTourPortal(node, 'chat'),
    [registerTourPortal],
  )
  const setOwnerPortalSlot = useCallback(
    (node: HTMLDivElement | null) => registerTourPortal(node, 'owner'),
    [registerTourPortal],
  )
  const setEnvironmentPortalSlot = useCallback(
    (node: HTMLDivElement | null) => registerTourPortal(node, 'environment'),
    [registerTourPortal],
  )
  const [strip, setStrip] = useState<HTMLDivElement | null>(null)
  const [chatPickerReady, setChatPickerReady] = useState(false)
  const [chatPickerFailed, setChatPickerFailed] = useState(false)
  const chatReadyFrameRef = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (chatReadyFrameRef.current !== null) cancelAnimationFrame(chatReadyFrameRef.current)
    },
    [],
  )
  const hasAutoSelectedRef = useRef(false)
  const selectedRepo = targetSelection.kind === 'repository' ? targetSelection.repository : undefined
  const canSelectChat = allowChat && selectedEnvironment?.kind === 'sandbox'
  // While the user is searching the repo picker, the "Chat" select-all option and
  // the owner subtitle are hidden so the result list reads as a flat repo list.
  // The search value only reaches this component through `repositoryLoader`, so we
  // track it there rather than reaching into ReposSelector's internal filter state.
  const [isSearching, setIsSearching] = useState(false)

  const handleTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection) => {
      if (selection.kind === 'repository' && !isRepoInScope(selection.repository, scopeOwner)) return
      hasAutoSelectedRef.current = true
      onTargetSelectionChange(selection)
      if (selection.kind === 'chat') {
        onBranchSelect(undefined)
      }
    },
    [onBranchSelect, onTargetSelectionChange, scopeOwner],
  )

  // Drop the target when the scope excludes it, falling back to Chat if allowed.
  useEffect(() => {
    if (isRepoInScope(selectedRepo, scopeOwner)) return
    handleTargetSelection(canSelectChat ? {kind: 'chat'} : {kind: 'unselected'})
    onBranchSelect(undefined)
  }, [scopeOwner, selectedRepo, canSelectChat, handleTargetSelection, onBranchSelect])

  const onLoad = useCallback(
    (loadedRepositories: AgentDropdownRepository[]) => {
      if (tourEnabled) setChatPickerFailed(false)
      if (tourEnabled && !chatPickerReady && chatReadyFrameRef.current === null) {
        chatReadyFrameRef.current = requestAnimationFrame(() => {
          chatReadyFrameRef.current = null
          setChatPickerReady(true)
        })
      }
      const repositories = loadedRepositories.filter(repo => isRepoInScope(repo, scopeOwner))
      if (!suppressAutoSelect && targetSelection.kind === 'unselected' && !hasAutoSelectedRef.current) {
        let initialRepo: AgentDropdownRepository | undefined
        if (initialRepoNWO) {
          initialRepo = repositories.find(repo => repo.nameWithOwner === initialRepoNWO)
          if (initialRepo && initialRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: initialRepo})
            onBranchSelect(initialBranch || initialRepo.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
          // The exact repository resolver owns explicit URL and host targets.
          // Do not let this picker's partial first page overwrite that selection.
          return
        }
        const lastUsedRepo = copilotLocalStorage.getLastUsedRepository()
        if (lastUsedRepo) {
          const loadedLastUsedRepo = repositories.find(
            repo => repo.nameWithOwner === `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`,
          )
          if (loadedLastUsedRepo && loadedLastUsedRepo.enabled) {
            onTargetSelectionChange({kind: 'repository', repository: loadedLastUsedRepo})
            onBranchSelect(loadedLastUsedRepo?.defaultBranch)
            hasAutoSelectedRef.current = true
            return
          }
        }
        const firstEnabledRepo = repositories.find(repo => repo.enabled)
        if (firstEnabledRepo) {
          onTargetSelectionChange({kind: 'repository', repository: firstEnabledRepo})
          onBranchSelect(firstEnabledRepo.defaultBranch)
          hasAutoSelectedRef.current = true
        }
      }
    },
    [
      initialRepoNWO,
      initialBranch,
      chatPickerReady,
      tourEnabled,
      onBranchSelect,
      onTargetSelectionChange,
      targetSelection.kind,
      scopeOwner,
      suppressAutoSelect,
    ],
  )

  const onError = useCallback(() => {
    if (chatReadyFrameRef.current !== null) cancelAnimationFrame(chatReadyFrameRef.current)
    chatReadyFrameRef.current = null
    setChatPickerFailed(true)
    setChatPickerReady(true)
  }, [])

  const repositoryLoader = useCallback(
    async (query: string, defaultOwner?: string, rejectOnHttpError = false) => {
      if (scopeOwner === null || scopeOwner === '') return []
      setIsSearching(query.trim().length > 0)
      const effectiveOwner = scopeOwner ?? defaultOwner
      const scopedQuery = scopedRepositoryQuery(query, scopeOwner)
      if (preferredRepositoryOwner) {
        return loadDestinationRepositories({
          query,
          preferredOwner: preferredRepositoryOwner,
          includedRepository: initialRepoNWO,
          rejectOnHttpError,
        })
      }
      const repos = rejectOnHttpError
        ? await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO, {rejectOnHttpError: true})
        : await queryForRepos(scopedQuery, effectiveOwner, initialRepoNWO)
      if (!scopeOwner) return repos
      return repos.filter(repo => isRepoInScope(repo, scopeOwner))
    },
    [initialRepoNWO, preferredRepositoryOwner, scopeOwner],
  )
  // ReposSelector can only disable rows, which keyboard users cannot reach, so
  // it omits Sandbox-disabled owners that ChatTargetPicker shows as inactive.
  const omitSandboxDisabled = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      if (!preferredRepositoryOwner || !sandboxDisabledOwners?.length) return repositories
      const disabledOwners = new Set(sandboxDisabledOwners.map(owner => owner.toLowerCase()))
      return repositories.filter(repo => !disabledOwners.has(repo.owner.toLowerCase()))
    },
    [preferredRepositoryOwner, sandboxDisabledOwners],
  )
  const reposSelectorRepositoryLoader = useCallback(
    async (query: string, defaultOwner?: string) => omitSandboxDisabled(await repositoryLoader(query, defaultOwner)),
    [omitSandboxDisabled, repositoryLoader],
  )
  const strictRepositoryLoader = useCallback(
    (query: string) => repositoryLoader(query, undefined, true),
    [repositoryLoader],
  )
  const tourRepositoryLoader = useCallback(
    async (query: string, defaultOwner?: string) => {
      try {
        return omitSandboxDisabled(await repositoryLoader(query, defaultOwner, true))
      } catch {
        if (!query.trim()) onError()
        return failedRepositoryLoad
      }
    },
    [omitSandboxDisabled, onError, repositoryLoader],
  )
  const onSelectorLoad = useCallback(
    (repositories: AgentDropdownRepository[]) => {
      if (repositories === failedRepositoryLoad) return
      onLoad(repositories)
    },
    [onLoad],
  )

  const repoPickerSubtitle = repoPickerScopeSubtitle(scopeOwner, currentUserLogin)

  const newRepoUrl = useMemo(
    () => buildNewRepositoryUrl(currentUserLogin, currentPrompt),
    [currentUserLogin, currentPrompt],
  )

  const hasPrimaryRepo = !!selectedRepo

  const newRepoLink = (
    <SelectPanel.SecondaryActionLink
      href={newRepoUrl}
      leadingVisual={PlusIcon}
      onClick={() => {
        sendEvent('agent_sessions.task_view', {target: 'NEW_REPOSITORY_LINK'})
      }}
    >
      New repository
    </SelectPanel.SecondaryActionLink>
  )

  const environmentPicker = showEnvironmentPicker ? (
    <EnvironmentPicker
      appearance="strip"
      selectedEnvironmentId={selectedEnvironment?.id ?? CLOUD_ENVIRONMENT_ID}
      selectedOption={selectedOption}
      design={design}
      isChatSelected={design === 'simplified' && targetSelection.kind === 'chat'}
      onSelectEnvironment={onSelectEnvironment}
      {...(environmentOptions ? {environments: environmentOptions} : {})}
      includeSandboxOption={includeSandboxOption}
      announceUnavailable={false}
      disabled={disabled}
    />
  ) : null

  return (
    <>
      <div ref={tourEnabled ? setStrip : undefined} className={styles.strip} data-testid="environment-strip">
        {design === 'default' && environmentPicker && (
          <>
            <OnboardingAnchor enabled={tourEnabled} id="environment">
              {environmentPicker}
            </OnboardingAnchor>
            {tourEnabled && <div ref={setEnvironmentPortalSlot} className={styles.tourPortal} />}
          </>
        )}

        {!hideRepoSelector && !hasPrimaryRepo && chatOwner && (
          <>
            <OnboardingAnchor enabled={tourEnabled} id="chat">
              <ChatTargetPicker
                chatOwner={chatOwner}
                chatSelected={targetSelection.kind === 'chat'}
                triggerText={targetSelection.kind === 'chat' ? `Chat (${chatOwner.label})` : 'Select repository'}
                triggerLabel={targetSelection.kind === 'chat' ? `Mode: Chat (${chatOwner.label})` : 'Select repository'}
                triggerClassName={styles.stripButton}
                disabled={disabled}
                repositoryLoader={strictRepositoryLoader}
                includedRepositoryNameWithOwner={initialRepoNWO}
                onLoad={onLoad}
                onError={tourEnabled ? onError : undefined}
                onSelectRepository={repo => {
                  handleTargetSelection({kind: 'repository', repository: repo})
                  onBranchSelect(repo.defaultBranch)
                }}
                onSelectChat={() => handleTargetSelection({kind: 'chat'})}
                secondaryAction={newRepoLink}
                scopeOwner={scopeOwner}
                preferredRepositoryOwner={preferredRepositoryOwner}
                sandboxDisabledOwners={sandboxDisabledOwners}
                currentUserLogin={currentUserLogin}
                showNotice={!preferredRepositoryOwner}
                loadingElement={<SkeletonBox width="120px" height="24px" />}
              />
            </OnboardingAnchor>
            {tourEnabled && <div ref={setChatPortalSlot} className={styles.tourPortal} />}
          </>
        )}

        {!hideRepoSelector && !hasPrimaryRepo && !chatOwner && (
          <>
            <OnboardingAnchor enabled={tourEnabled} id="chat">
              <ReposSelector<AgentDropdownRepository>
                repositoryLoader={tourEnabled ? tourRepositoryLoader : reposSelectorRepositoryLoader}
                selectionVariant="single"
                iconOnly={false}
                responsiveStyle="full"
                selectAllOption={canSelectChat && !isSearching}
                selectAllOptionPlacement="inline"
                selectAllOptionText="Chat"
                selectAllOptionLeadingVisual={CommentDiscussionIcon}
                selectAllOptionSelected={targetSelection.kind === 'chat'}
                buttonText={targetSelection.kind === 'chat' ? 'Chat' : 'Select repository'}
                onSelect={repo => {
                  if (repo) {
                    handleTargetSelection({kind: 'repository', repository: repo})
                    onBranchSelect(repo.defaultBranch)
                  } else if (canSelectChat) {
                    handleTargetSelection({kind: 'chat'})
                  }
                }}
                currentSelection={selectedRepo}
                width="medium"
                visibleRowCount={6}
                additionalButtonProps={{
                  'aria-label': targetSelection.kind === 'chat' ? 'Mode: Chat' : 'Select repository',
                  leadingVisual: targetSelection.kind === 'chat' ? CommentDiscussionIcon : undefined,
                  className: styles.stripButton,
                  variant: 'invisible',
                  size: 'small',
                  disabled,
                }}
                onLoad={tourEnabled ? onSelectorLoad : onLoad}
                loadingElement={<SkeletonBox width="120px" height="24px" />}
                secondaryAction={newRepoLink}
                subtitle={isSearching || preferredRepositoryOwner ? undefined : repoPickerSubtitle}
                restrictionOwner={preferredRepositoryOwner ? undefined : (scopeOwner ?? undefined)}
              />
            </OnboardingAnchor>
            {tourEnabled && <div ref={setChatPortalSlot} className={styles.tourPortal} />}
          </>
        )}

        {hasPrimaryRepo && (
          <>
            <OnboardingAnchor enabled={tourEnabled && !hideRepoSelector} id="chat">
              <RepoBranchPill
                pair={{id: 'primary', repo: selectedRepo, branch: selectedBranch || selectedRepo.defaultBranch}}
                onRepoChange={(_id, repo) => {
                  handleTargetSelection({kind: 'repository', repository: repo})
                  onBranchSelect(repo.defaultBranch)
                }}
                onBranchChange={(_id, branch) => onBranchSelect(branch)}
                onRemove={() => {
                  handleTargetSelection({kind: 'unselected'})
                }}
                removable={false}
                hideRepo={hideRepoSelector}
                initialRepoNWO={initialRepoNWO}
                onLoad={onLoad}
                onError={tourEnabled ? onError : undefined}
                secondaryAction={newRepoLink}
                disabled={disabled}
                scopeOwner={scopeOwner}
                repoPickerSubtitle={repoPickerSubtitle}
                allowChat={canSelectChat}
                onChatSelect={() => handleTargetSelection({kind: 'chat'})}
                chatOwner={chatOwner}
                tourPortalSlot={
                  tourEnabled && !hideRepoSelector ? (
                    <div ref={setChatPortalSlot} className={styles.tourPortal} />
                  ) : undefined
                }
              />
            </OnboardingAnchor>
          </>
        )}

        {targetSelection.kind === 'chat' && !chatOwner && scopePicker ? (
          <>
            <OnboardingAnchor enabled={tourEnabled} id="owner">
              {scopePicker}
            </OnboardingAnchor>
            {tourEnabled && <div ref={setOwnerPortalSlot} className={styles.tourPortal} />}
          </>
        ) : null}
        {design === 'simplified' && environmentPicker && (
          <>
            <OnboardingAnchor enabled={tourEnabled} id="environment">
              {environmentPicker}
            </OnboardingAnchor>
            {tourEnabled && <div ref={setEnvironmentPortalSlot} className={styles.tourPortal} />}
          </>
        )}
      </div>
      {tourEnabled && Object.values(portalSlots).some(Boolean) && (
        <EnvironmentStripTour
          scope={strip}
          portalSlots={portalSlots}
          disabled={disabled}
          submitting={submitting}
          chatOwner={Boolean(chatOwner)}
          chatPickerReady={chatPickerReady || hideRepoSelector === true}
          chatPickerFailed={chatPickerFailed}
          textAreaRef={textAreaRef}
          onVisibilityChange={onTourVisibilityChange ?? ignoreTourVisibility}
        />
      )}
    </>
  )
}
